import React, { useState } from 'react';
import { Grid, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DropzoneArea } from 'react-mui-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import fileDownload from 'js-file-download';
import FormData from 'form-data';
import { useSnackbar } from 'notistack';
import {
    useDownloadPositionTemplateMutation,
    useDownloadPositionTemplateWithCompaFieldsMutation,
} from '../../../api/download';
import { useLoadPositionDataActionMutation } from '../../../api/positions';
import { updatePositions } from '../../../store/slices/bulkUploadPositions';
import PermissionChecker from '../../../components/common/PermissionChecker';
import { hasPermission } from '../../../utils/permission';

const PositionBulkUploadDialog = ({ openBulkUploadDialog, onResult }) => {
    const dispatch = useDispatch();
    const [files, setFiles] = useState(null);
    const { enqueueSnackbar } = useSnackbar();

    const authorities = useSelector((state) => state.session.user.authorities);
    const userHasCompaDownloadPermisson = hasPermission(authorities, [
        'ROLE_BASE_PERMISSION_RECRUIT_INDEX_COMPA_DOWNLOAD_POSITION_TEMPLATE',
    ]);

    const [downloadPositionTemplate, { isLoading: isDownloading }] = useDownloadPositionTemplateMutation();
    const [downloadPositionTemplateWithCompaFields, { isLoading: isDownloadingWithCompaFields }] =
        useDownloadPositionTemplateWithCompaFieldsMutation();
    const [loadPositionDataAction, { isLoading: isLoading }] = useLoadPositionDataActionMutation();

    const handleFiles = (files) => {
        setFiles(files[0]);
    };

    const downloadSampleFile = async () => {
        try {
            const response = userHasCompaDownloadPermisson
                ? await downloadPositionTemplateWithCompaFields().unwrap()
                : await downloadPositionTemplate().unwrap();
            if (!response) return;
            if (response.status === 'ERROR') throw new Error(response?.message);

            fileDownload(new Blob([response]), 'Template-PositionData.xlsx');
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Download failed', { variant: 'error' });
        }
    };

    const handleUpload = async () => {
        try {
            const formData = new FormData();
            formData.append('dataFile', files);

            const response = await loadPositionDataAction(formData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') {
                enqueueSnackbar(response?.message, { variant: 'error' });
                dispatch(updatePositions(response));
                //navigate('/positions/bulk-modify/preview');
            } else {
                enqueueSnackbar(response?.message, { variant: 'success' });
                onResult(true);
            }
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Upload failed', { variant: 'error' });
        }
    };
    return (
        <Dialog open={openBulkUploadDialog} fullWidth maxWidth="sm" onClose={() => onResult(null)}>
            <DialogTitle>Bulk Upload Positions</DialogTitle>
            <DialogContent>
                <PermissionChecker
                    requiredPermissions={['ROLE_BASE_PERMISSION_RECRUIT_INDEX_DOWNLOAD_POSITION_TEMPLATE']}
                    noAuthRedirect={false}
                >
                    <DialogContentText>
                        To download a template file
                        <LoadingButton
                            loading={isDownloading || isDownloadingWithCompaFields}
                            variant="text"
                            color="primary"
                            onClick={downloadSampleFile}
                        >
                            Click here
                        </LoadingButton>
                    </DialogContentText>
                </PermissionChecker>
                <Grid container justifyContent="center" alignItems="center" mt={2}>
                    <Grid item xs={12}>
                        <DropzoneArea
                            dropzoneText="Drag and drop a file here or click"
                            filesLimit={1}
                            onChange={handleFiles}
                            showAlerts={false}
                            showPreviews={true}
                            showPreviewsInDropzone={false}
                            useChipsForPreview
                            previewGridProps={{
                                container: {
                                    spacing: 1,
                                    direction: 'row',
                                },
                            }}
                            previewText="Selected files"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <LoadingButton
                    disabled={!files}
                    loading={isLoading}
                    variant="contained"
                    color="primary"
                    onClick={handleUpload}
                >
                    Upload
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default PositionBulkUploadDialog;
